import { useState, useMemo } from 'react'
import { parseDateString } from '../utils/dateUtils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { FileText, Users, Package, CheckCircle2 } from 'lucide-react'

export default function ImportWizardV2PreviewModal({
  open,
  onClose,
  csvData,
  recordStatuses,
  customerLookup,
  productConfigs,
  productMatches,
  getCurrentRecordData,
  onConfirm,
  importing
}) {
  // Get only matched records (ones that will be imported)
  const matchedRecords = useMemo(() => {
    if (!csvData) return []

    return csvData
      .map((record, index) => ({
        record,
        index,
        data: getCurrentRecordData(index)
      }))
      .filter(({ index }) => recordStatuses[index] === 'matched')
  }, [csvData, recordStatuses, getCurrentRecordData])

  // Get unique customers
  const uniqueCustomers = useMemo(() => {
    const customerMap = new Map()

    matchedRecords.forEach(({ data }) => {
      const customer = customerLookup[data.customerId]
      if (customer && !customerMap.has(customer.id)) {
        customerMap.set(customer.id, {
          ...customer,
          recordCount: matchedRecords.filter(r => r.data.customerId === customer.id).length
        })
      }
    })

    return Array.from(customerMap.values())
  }, [matchedRecords, customerLookup])

  // Get unique products with configs
  const uniqueProducts = useMemo(() => {
    const productMap = new Map()

    matchedRecords.forEach(({ data }) => {
      const strain = data.strain?.trim() || ''
      const productType = data.product?.trim() || ''
      const key = `${strain}|||${productType}`

      if (!productMap.has(key)) {
        const config = productConfigs[key]
        const isMatched = productMatches[key] !== undefined

        productMap.set(key, {
          key,
          strain,
          productType,
          config,
          isMatched,
          recordCount: matchedRecords.filter(r => {
            const rStrain = r.data.strain?.trim() || ''
            const rType = r.data.product?.trim() || ''
            return `${rStrain}|||${rType}` === key
          }).length,
          totalQuantity: matchedRecords
            .filter(r => {
              const rStrain = r.data.strain?.trim() || ''
              const rType = r.data.product?.trim() || ''
              return `${rStrain}|||${rType}` === key
            })
            .reduce((sum, r) => sum + parseFloat(r.data.quantity || 0), 0)
        })
      }
    })

    return Array.from(productMap.values()).sort((a, b) => b.recordCount - a.recordCount)
  }, [matchedRecords, productConfigs, productMatches])

  const formatDate = (dateString) => {
    const date = parseDateString(dateString)
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const formatCurrency = (value) => {
    // Strip currency symbols and commas before parsing
    const cleanValue = typeof value === 'string'
      ? value.replace(/[£$,]/g, '')
      : value
    return `£${parseFloat(cleanValue || 0).toFixed(2)}`
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="odin-modal-large max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Preview Import Data
          </DialogTitle>
          <DialogDescription>
            Review the {matchedRecords.length} records that will be imported. Check for any data issues before confirming.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="sales" className="flex-1">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="sales">
              <FileText className="w-4 h-4 mr-2" />
              Sales ({matchedRecords.length})
            </TabsTrigger>
            <TabsTrigger value="customers">
              <Users className="w-4 h-4 mr-2" />
              Customers ({uniqueCustomers.length})
            </TabsTrigger>
            <TabsTrigger value="products">
              <Package className="w-4 h-4 mr-2" />
              Products ({uniqueProducts.length})
            </TabsTrigger>
          </TabsList>

          {/* Sales Tab */}
          <TabsContent value="sales" className="mt-4">
            <div className="h-[500px] overflow-y-auto border rounded-md">
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow>
                    <TableHead className="w-28">Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Branch</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Qty (kg)</TableHead>
                    <TableHead className="text-right">Price/kg</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {matchedRecords.map(({ index, data }) => {
                    const customer = customerLookup[data.customerId]
                    // Strip currency symbols from price before calculation
                    const cleanPrice = typeof data.price === 'string'
                      ? data.price.replace(/[£$,]/g, '')
                      : data.price
                    const total = parseFloat(data.quantity || 0) * parseFloat(cleanPrice || 0)

                    return (
                      <TableRow key={index}>
                        <TableCell className="text-sm">
                          {formatDate(data.date)}
                        </TableCell>
                        <TableCell>
                          {customer ? (
                            <div>
                              <div className="font-medium">{customer.name}</div>
                              {/* Architecture migration: customer_type moved to branch level */}
                              {/* Display first branch's customer type (most customers have single branch) */}
                              {customer.branches?.[0]?.customer_types?.name && (
                                <div className="text-xs text-muted-foreground">
                                  {customer.branches[0].customer_types.name}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-red-600 text-sm">Not found</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {data.branch || data.branchName ? (
                            <span className="text-sm">{data.branch || data.branchName}</span>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div className="font-medium">{data.strain}</div>
                            <div className="text-xs text-muted-foreground">{data.product}</div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{data.quantity}</TableCell>
                        <TableCell className="text-right">{formatCurrency(data.price)}</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(total)}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>

            {matchedRecords.length === 0 && (
              <div className="flex items-center justify-center py-12 border rounded-md">
                <div className="text-center text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No sales records to import</p>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers" className="mt-4">
            <div className="h-[500px] overflow-y-auto border rounded-md p-4 space-y-4">
              {uniqueCustomers.map(customer => (
                <div key={customer.id} className="border rounded-lg p-4 bg-white">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h4 className="font-semibold text-lg">{customer.name}</h4>
                      {/* Architecture migration: customer_type moved to branch level */}
                      {/* Display first branch's customer type (most customers have single branch) */}
                      {customer.branches?.[0]?.customer_types?.name && (
                        <Badge variant="outline" className="mt-1">
                          {customer.branches[0].customer_types.name}
                        </Badge>
                      )}
                    </div>
                    <Badge variant="secondary">
                      {customer.recordCount} {customer.recordCount === 1 ? 'sale' : 'sales'}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                    {customer.email && (
                      <div>
                        <span className="text-muted-foreground">Email:</span>
                        <div className="font-medium">{customer.email}</div>
                      </div>
                    )}
                    {customer.phone && (
                      <div>
                        <span className="text-muted-foreground">Phone:</span>
                        <div className="font-medium">{customer.phone}</div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {uniqueCustomers.length === 0 && (
              <div className="flex items-center justify-center py-12 border rounded-md">
                <div className="text-center text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No customers in selected records</p>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" className="mt-4">
            <div className="h-[500px] overflow-y-auto border rounded-md">
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow>
                    <TableHead>Strain</TableHead>
                    <TableHead>Product Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Total Quantity</TableHead>
                    <TableHead className="text-center">Records</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {uniqueProducts.map(product => (
                    <TableRow key={product.key}>
                      <TableCell className="font-medium">{product.strain}</TableCell>
                      <TableCell>
                        {product.productType ? (
                          <Badge variant="outline">{product.productType}</Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {product.isMatched ? (
                          <Badge variant="outline" className="text-blue-600 border-blue-600">
                            Existing
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-green-600 border-green-600">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            New
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {product.totalQuantity.toFixed(2)} kg
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">
                          {product.recordCount}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {uniqueProducts.length === 0 && (
              <div className="flex items-center justify-center py-12 border rounded-md">
                <div className="text-center text-muted-foreground">
                  <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No products in selected records</p>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={importing}
          >
            Back
          </Button>
          <Button
            onClick={onConfirm}
            disabled={importing || matchedRecords.length === 0}
          >
            {importing ? 'Importing...' : `Confirm & Import ${matchedRecords.length} Records`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
